import {UI} from '../libs/palette.js';
import {PHONE} from './RotatePrompt';

/**
 * SidewaysNudge: "BEST PLAYED SIDEWAYS" in the start screen's top bar, on a
 * phone or tablet held upright. The game itself is landscape only (the rotate
 * prompt covers it upright), so this says so before START rather than after.
 *
 * It takes no height (the bar's left slot is empty), so it never pushes the
 * form or START down. CSS decides when it shows: a touch screen held upright,
 * gone the moment the device turns. Its X hides it for the rest of the visit.
 */

const {INK, PAPER, LIME, SLATE} = UI;

const CLOSED_KEY = 'receipthunt.nudgeClosed';

export const NUDGE_STYLES = `
#menu .nudge { display: none; }
@media (orientation: portrait) and (pointer: coarse) {
  #menu .nudge { display: flex; align-items: center; gap: 8px; color: ${LIME}; font-size: 8px; line-height: 1.5; letter-spacing: 0; }
  #menu .topbar .tl { min-width: 0; }
}
/* a phone hides the middle of the bar, so the nudge and the music button share it */
@media (orientation: portrait) and (pointer: coarse) and (max-width: 700px) {
  #menu .topbar:has(.nudge) { grid-template-columns: minmax(0, 1fr) auto; }
}
#menu .nudge .nd-ico { flex: none; width: 18px; height: 18px; }
#menu .nudge .rp-phone { width: 18px; height: 18px; image-rendering: pixelated; display: block; transform: rotate(-90deg); }
#menu .topbar .nudge .nd-txt { min-width: 0; white-space: normal; }
#menu .nudge .nd-x {
  flex: none; position: relative; font: inherit; font-size: 9px; line-height: 1; color: ${PAPER};
  background: transparent; border: 2px solid ${SLATE}; padding: 4px 5px; cursor: pointer;
}
/* a 44px finger target round the small box */
#menu .nudge .nd-x::after { content: ''; position: absolute; left: 50%; top: 50%; width: 44px; height: 44px; transform: translate(-50%, -50%); }
#menu .nudge .nd-x:focus-visible { outline: 2px solid ${LIME}; outline-offset: 2px; }
/* the phone turns on its side once, a beat after the screen opens */
@media (prefers-reduced-motion: no-preference) {
  #menu .nudge .rp-phone { animation: nd-turn .7s ease-in-out .8s both; }
}
@keyframes nd-turn { from { transform: rotate(0deg); } to { transform: rotate(-90deg); } }
#menu .nudge .nd-x:hover { color: ${LIME}; border-color: ${LIME}; background: ${INK}; }
`;

/**
 * @returns {boolean} whether the player closed the nudge this visit
 */
function closed() {
  try {
    return sessionStorage.getItem(CLOSED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * The nudge's markup for the top bar's left slot; empty once closed.
 * @returns {string}
 */
export function nudgeMarkup() {
  if (closed()) {
    return '';
  }
  return `<span class="nudge" role="note"><span class="nd-ico">${PHONE}</span>
    <span class="nd-txt">BEST PLAYED SIDEWAYS</span>
    <button type="button" class="nd-x" aria-label="Hide this tip">X</button></span>`;
}

/**
 * Wires the X: it removes the nudge and remembers that for the visit.
 * @param {HTMLElement} root the start screen
 */
export function bindNudge(root) {
  const x = root.querySelector('.nudge .nd-x');
  if (!x) {
    return;
  }
  x.addEventListener('click', () => {
    const nudge = root.querySelector('.nudge');
    if (nudge) {
      nudge.remove();
    }
    try {
      sessionStorage.setItem(CLOSED_KEY, '1');
    } catch (e) {
      // private browsing: it just comes back next time
    }
  });
}
